/*
PURPOSE: Grid layout component for displaying multiple people in a responsive grid

USE CASES:
- Display lists of users in alignment grids
- Display lists of contributors on artist pages
- Provide loading and empty states for people lists

DOMAIN BOUNDARIES:
- Responsible for grid layout and organization only
- Delegates individual person rendering to PersonCard
- Does NOT handle data fetching

ASSUMPTIONS:
- Receives array of people with userId and display info
- Grid should be responsive across different screen sizes
- Loading and empty states should match CoralGrid patterns
*/

import React from 'react';
import PersonCard from '../PersonCard/PersonCard';

interface PersonData {
  userId: string;
  displayName?: string;
  firstName?: string;
  createdAt?: string;
}

interface PersonGridProps {
  people: PersonData[];
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
}

const PersonGrid: React.FC<PersonGridProps> = ({
  people,
  isLoading = false,
  emptyMessage = 'No people to display',
  className = ''
}) => {
  // Show loading state
  if (isLoading) {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${className}`}>
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="bg-white rounded-lg p-4 shadow-sm animate-pulse">
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
              <div className="w-full space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Show empty state
  if (people.length === 0) {
    return (
      <div className={`text-center py-16 ${className}`}>
        <div className="text-gray-400 mb-4">
          <svg
            className="mx-auto h-16 w-16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </div>
        <p className="text-gray-500 text-lg">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${className}`}>
      {people.map((person) => (
        <PersonCard
          key={person.userId}
          userId={person.userId}
          displayName={person.displayName}
          firstName={person.firstName}
          createdAt={person.createdAt}
        />
      ))}
    </div>
  );
};

export default PersonGrid;
